import { Square, Circle, MoveUpRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ShapeType, PenThickness } from "./types";
import { PEN_THICKNESS_PRESETS } from "./types";

interface ShapePickerBarProps {
    selectedShapeType: ShapeType;
    onShapeTypeChange: (shapeType: ShapeType) => void;
    selectedThickness: PenThickness;
    onThicknessChange: (thickness: PenThickness) => void;
}

const SHAPE_OPTIONS: { type: ShapeType; icon: React.ElementType; label: string }[] = [
    { type: "rectangle", icon: Square, label: "Rectangle" },
    { type: "circle", icon: Circle, label: "Circle" },
    { type: "arrow", icon: MoveUpRight, label: "Arrow" },
];

export function ShapePickerBar({
    selectedShapeType,
    onShapeTypeChange,
    selectedThickness,
    onThicknessChange,
}: ShapePickerBarProps) {
    return (
        <div className="flex items-center w-full">
            {/* Shape type buttons */}
            <div className="flex items-center gap-1">
                {SHAPE_OPTIONS.map(({ type, icon: Icon, label }) => (
                    <button
                        key={type}
                        type="button"
                        onClick={() => onShapeTypeChange(type)}
                        className={cn(
                            "w-10 h-10 rounded-full flex items-center justify-center transition-all touch-manipulation",
                            selectedShapeType === type
                                ? "bg-white text-black"
                                : "text-white/60"
                        )}
                        aria-label={label}
                    >
                        <Icon className="w-5 h-5" />
                    </button>
                ))}
            </div>

            {/* Separator */}
            <div className="w-px h-6 bg-white/20 mx-3" />

            {/* Thickness dots */}
            <div className="flex items-center gap-1 flex-1 justify-evenly">
                {PEN_THICKNESS_PRESETS.map((thickness) => (
                    <button
                        key={thickness}
                        type="button"
                        onClick={() => onThicknessChange(thickness)}
                        className={cn(
                            "w-9 h-9 rounded-full flex items-center justify-center transition-all touch-manipulation",
                            selectedThickness === thickness
                                ? "ring-2 ring-white bg-white/20"
                                : "bg-transparent"
                        )}
                        aria-label={`Thickness ${thickness}`}
                    >
                        <div
                            className="rounded-full bg-white"
                            style={{
                                width: Math.min(thickness * 1.5 + 6, 24),
                                height: thickness * 0.8,
                            }}
                        />
                    </button>
                ))}
            </div>
        </div>
    );
}
